// [C36] D09 T-13 — the customer statement's dual-currency settlement box (old `getStructuredSummary`,
// original/src/components/ProfessionalPrint.tsx:270-420, rendered at :1408-1545), rebuilt on structured data (D-148):
// the figures come from the server's statement (`GET /customers/:id/statement` totals and running balances), the
// direction is a value ("lakum" / "alaykum" / "zero"), never re-parsed from labels (old R-R12, F-222).
//
// Old semantics kept (EXPLICIT):
//   - "مجموع المبالغ الصادرة له" / "مجموع المبالغ المستلمة منه" = the listed movements out to / in from the customer
//     (App.tsx:23139-23151);
//   - "الرصيد قبل التسوية" = the running balance before the last listed movement, 0 when there is one movement and
//     no earlier one (App.tsx:23153-23165);
//   - "آخر حركة" = |after − before| per currency when no explicit last movement is given (PP:356-366);
//   - the badge: any currency owed by the customer → "(عليكم بعد التسوية)", else any due to the customer →
//     "(لكم بعد التسوية)", else "(متطابق / صفر)" (PP:384-389, translated to the natural sign of D-191: a trust
//     above zero and a debt below zero are "لكم").
// Changed (CHANGE, D-148): the "after" label follows the direction (old: always "عليكم بعد التسوية" on a statement,
// even when the badge said "لكم"); words: one "فقط … و … لا غير" phrase when both currencies are on the same side
// (D-326), one phrase per side otherwise; commission: the statement has no per-row commission (V-62), so the line
// prints "-" instead of a made-up 0.

export type SettlementSide = 'lakum' | 'alaykum' | 'zero';

/** One figure in both currencies, preformatted by the builder ("250,000 د.ع", "100.50 $"). */
export interface SettlementPair {
  readonly iqd: string;
  readonly usd: string;
}

export interface StatementSettlement {
  readonly totalOut: SettlementPair;
  readonly totalIn: SettlementPair;
  /** null → the line prints "-" (no per-row commission in the new statement, or the office hides profit, D-148). */
  readonly commission: SettlementPair | null;
  readonly before: SettlementPair;
  readonly lastMovement: SettlementPair;
  readonly after: SettlementPair;
  readonly side: SettlementSide;
}

export interface SettlementWordsItem {
  readonly lines: ReadonlyArray<{ readonly amount: string; readonly currency: 'IQD' | 'USD' }>;
  readonly side: 'lakum' | 'alaykum' | null;
}

// ---- exact decimal strings (scale 10^6, the posting precision ceiling); no floating point (ADR-02 §2.9)
const SCALE = 6;
function toScaled(v: string): bigint {
  const m = /^(-?)(\d+)(?:\.(\d{1,6}))?$/.exec(v.trim());
  if (!m) throw new Error(`settlement: not a decimal "${v}"`);
  const n = BigInt(m[2]! + (m[3] ?? '').padEnd(SCALE, '0'));
  return m[1] === '-' ? -n : n;
}
function fromScaled(n: bigint): string {
  const neg = n < 0n;
  const s = (neg ? -n : n).toString().padStart(SCALE + 1, '0');
  const int = s.slice(0, -SCALE);
  const frac = s.slice(-SCALE).replace(/0+$/, '');
  return `${neg ? '-' : ''}${int}${frac ? `.${frac}` : ''}`;
}
const sub = (a: string, b: string): string => fromScaled(toScaled(a) - toScaled(b));
const abs = (a: string): string => (a.startsWith('-') ? a.slice(1) : a);
const sign = (a: string): -1 | 0 | 1 => {
  const n = toScaled(a);
  return n === 0n ? 0 : n < 0n ? -1 : 1;
};

/** Direction of one balance in the natural sign (D-191): trust > 0 and debt < 0 are due to the customer (لكم). */
export function balanceSide(kind: 'debt' | 'trust', amount: string): SettlementSide {
  const s = sign(amount);
  if (s === 0) return 'zero';
  return (kind === 'trust') === (s > 0) ? 'lakum' : 'alaykum';
}

/** The badge of the old template (PP:384-389): owed in any currency wins, then due, then balanced. */
export function settlementSide(sides: readonly SettlementSide[]): SettlementSide {
  if (sides.includes('alaykum')) return 'alaykum';
  if (sides.includes('lakum')) return 'lakum';
  return 'zero';
}

export interface CustomerSettlementInput {
  readonly kind: 'debt' | 'trust';
  /** Server totals of the listed movements in the natural sign: `in*` = the balance grew, `out*` = it shrank. */
  readonly totals: { readonly inIqd: string; readonly outIqd: string; readonly inUsd: string; readonly outUsd: string; readonly closingIqd: string; readonly closingUsd: string };
  /** Running balances before the last listed movement ("0" when there is none). */
  readonly beforeIqd: string;
  readonly beforeUsd: string;
  /** Office formatting of a signed decimal string ("-250,000"); the unit is appended by the builder. */
  readonly fmt: (amount: string) => string;
  readonly units: { readonly iqd: string; readonly usd: string };
  /** D-066: the office hides USD dealing — the USD figures and words are left out. */
  readonly usdHidden?: boolean;
}

/** The settlement box and the words request (`POST /documents/words`) of one customer account's statement. */
export function customerSettlement(x: CustomerSettlementInput): { settlement: StatementSettlement; words: SettlementWordsItem[] } {
  const pair = (iqd: string, usd: string): SettlementPair => ({ iqd: `${x.fmt(iqd)} ${x.units.iqd}`, usd: x.usdHidden ? '-' : `${x.fmt(usd)} ${x.units.usd}` });
  const t = x.totals;
  // "issued to him" = value the office gave: a debt grows, a trust shrinks; "received from him" the other way
  const issued = x.kind === 'debt' ? pair(t.inIqd, t.inUsd) : pair(t.outIqd, t.outUsd);
  const received = x.kind === 'debt' ? pair(t.outIqd, t.outUsd) : pair(t.inIqd, t.inUsd);
  const sideIqd = balanceSide(x.kind, t.closingIqd);
  const sideUsd = x.usdHidden ? 'zero' : balanceSide(x.kind, t.closingUsd);
  const settlement: StatementSettlement = {
    totalOut: issued,
    totalIn: received,
    commission: null,
    before: pair(x.beforeIqd, x.beforeUsd),
    lastMovement: pair(abs(sub(t.closingIqd, x.beforeIqd)), abs(sub(t.closingUsd, x.beforeUsd))),
    after: pair(t.closingIqd, t.closingUsd),
    side: settlementSide([sideIqd, sideUsd]),
  };
  const toSide = (s: SettlementSide) => (s === 'zero' ? null : s);
  const iqdLine = sideIqd === 'zero' ? null : { amount: abs(t.closingIqd), currency: 'IQD' as const };
  const usdLine = sideUsd === 'zero' ? null : { amount: abs(t.closingUsd), currency: 'USD' as const };
  const words: SettlementWordsItem[] =
    iqdLine && usdLine && sideIqd === sideUsd
      ? [{ lines: [iqdLine, usdLine], side: toSide(sideIqd) }]
      : [...(iqdLine ? [{ lines: [iqdLine], side: toSide(sideIqd) }] : []), ...(usdLine ? [{ lines: [usdLine], side: toSide(sideUsd) }] : [])];
  return { settlement, words }; // both balances at zero → no words line (nothing to write out)
}
